import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { focusManager, onlineManager, QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { PropsWithChildren, useState } from 'react';
import AppProvider from './AppContext';
import { RelationProvider } from './RelationContext';
import { getUserScopedKey } from '@/utils';

const IS_WEBVIEW_STORAGE_KEY = 'isWebView';
const WEBVIEW_QUERY_RESUME_DELAY_MS = 500;

const isWebViewRuntime = () => {
  const searchParams = new URLSearchParams(window.location.search);
  return (
    searchParams.get('webview') === 'true' ||
    sessionStorage.getItem(IS_WEBVIEW_STORAGE_KEY) === 'true'
  );
};

const setupWebViewQueryLifecycleHandlers = () => {
  if (!isWebViewRuntime()) {
    return;
  }

  focusManager.setEventListener((handleFocus) => {
    let focusTimeout: ReturnType<typeof setTimeout> | null = null;

    const clearFocusTimeout = () => {
      if (focusTimeout) {
        clearTimeout(focusTimeout);
        focusTimeout = null;
      }
    };

    const handleVisibilityChange = () => {
      clearFocusTimeout();
      if (document.visibilityState === 'visible') {
        focusTimeout = setTimeout(() => {
          handleFocus(true);
        }, WEBVIEW_QUERY_RESUME_DELAY_MS);
        return;
      }

      handleFocus(false);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearFocusTimeout();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  });

  onlineManager.setEventListener((setOnline) => {
    let onlineTimeout: ReturnType<typeof setTimeout> | null = null;

    const clearOnlineTimeout = () => {
      if (onlineTimeout) {
        clearTimeout(onlineTimeout);
        onlineTimeout = null;
      }
    };

    const handleOnline = () => {
      clearOnlineTimeout();
      onlineTimeout = setTimeout(() => {
        setOnline(navigator.onLine);
      }, WEBVIEW_QUERY_RESUME_DELAY_MS);
    };

    const handleOffline = () => {
      clearOnlineTimeout();
      setOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      clearOnlineTimeout();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  });
};

setupWebViewQueryLifecycleHandlers();

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30, // 30 seconds
      gcTime: 1000 * 60 * 30, // 30 minutes
      refetchOnWindowFocus: false,
    },
  },
});

const CombinedProvider = ({ children }: PropsWithChildren<unknown>) => {
  const [sessionStoragePersistor] = useState(() =>
    createSyncStoragePersister({
      storage: window.sessionStorage,
      key: getUserScopedKey('REACT_QUERY_OFFLINE_CACHE'),
    }),
  );

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        maxAge: 1000 * 60 * 30, // 30 minutes
        persister: sessionStoragePersistor,
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            return (
              query.queryKey.includes('persist') &&
              query.state.status !== 'pending'
            );
          },
          shouldDehydrateMutation: (mutation) => {
            return (
              (mutation?.options?.mutationKey?.includes?.('persist') ??
                false) &&
              mutation.state.status !== 'pending'
            );
          },
        },
      }}
    >
      <AppProvider>
        <RelationProvider>{children}</RelationProvider>
      </AppProvider>
    </PersistQueryClientProvider>
  );
};

export default CombinedProvider;
